import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import { TenantProvider, useTenant } from "@/components/TenantProvider";
import { TenantLayout } from "@/components/TenantLayout";
import { AuthProvider } from "@/components/AuthProvider";
import { ThemeProvider } from "@/components/ThemeProvider";
import { TranslationProvider } from "@/lib/i18n";
import { RequireAuth, RequireAdmin, RedirectIfAuth } from "@/components/RouteGuard";
import AdminGuard from "@/components/AdminGuard";
import ClientLayout from "@/components/ClientLayout";
import AdminLayout from "@/components/AdminLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ProfilePage from "@/pages/ProfilePage";

/* ═══ LAZY LOADED — Isolasi per tenant ═══ */
const LandingRegistry = lazy(() => import("@/pages/LandingRegistry"));
const ExplorePageRouter = lazy(() => import("@/pages/PageRegistry").then(m => ({ default: m.ExplorePageRouter })));
const BookingPageRouter = lazy(() => import("@/pages/PageRegistry").then(m => ({ default: m.BookingPageRouter })));
const DashboardPageRouter = lazy(() => import("@/pages/PageRegistry").then(m => ({ default: m.DashboardPageRouter })));
const SuperAdminDashboard = lazy(() => import("@/pages/SuperAdminDashboard"));

/* ── Photography admin (Adara) ── */
const AdaraAdminDashboard = lazy(() => import("@/tenants/adara/admin/Dashboard"));
const AdaraAdminBookings = lazy(() => import("@/tenants/adara/admin/Bookings"));
const AdaraAdminPortfolio = lazy(() => import("@/tenants/adara/admin/Portfolio"));
const AdaraAdminHomepage = lazy(() => import("@/tenants/adara/admin/Homepage"));
const AdaraAdminServices = lazy(() => import("@/tenants/adara/admin/Services"));
const AdaraAdminUsers = lazy(() => import("@/tenants/adara/admin/Users"));

/* ── Salon admin (Vasalon) ── */
const SalonAdminDashboard = lazy(() => import("@/tenants/vasalon/admin/Dashboard"));
const SalonAdminBookings = lazy(() => import("@/tenants/vasalon/admin/Bookings"));
const SalonAdminServices = lazy(() => import("@/tenants/vasalon/admin/Services"));
const SalonAdminUsers = lazy(() => import("@/tenants/vasalon/admin/Users"));

/* ── Education admin (SMKN3) ── */
const Smkn3AdminDashboard = lazy(() => import("@/tenants/smkn3/admin/Dashboard"));

/* ── Personal admin (Ipung) ── */
const IpungAdminLMS = lazy(() => import("@/tenants/ipung/admin/LMS"));
const IpungAdminBlog = lazy(() => import("@/tenants/ipung/admin/Blog"));
const IpungAdminModules = lazy(() => import("@/tenants/ipung/admin/Modules"));
const IpungAdminCerts = lazy(() => import("@/tenants/ipung/admin/Certifications"));

/* ── Public pages ── */
const IpungBlog = lazy(() => import("@/tenants/ipung/pages/Blog"));
const IpungBlogDetail = lazy(() => import("@/tenants/ipung/pages/BlogDetail"));
const IpungLearn = lazy(() => import("@/tenants/ipung/pages/Learn"));
const IpungModule = lazy(() => import("@/tenants/ipung/pages/ModuleDetail"));
const IpungTools = lazy(() => import("@/tenants/ipung/pages/Tools"));
const IpungContent = lazy(() => import("@/tenants/ipung/pages/Content"));
const IpungLive = lazy(() => import("@/tenants/ipung/pages/Live"));

const TkjLanding = lazy(() => import("@/tenants/smkn3/pages/tkj/Landing"));
const TkjBerita = lazy(() => import("@/tenants/smkn3/pages/tkj/Berita"));
const TkjGuru = lazy(() => import("@/tenants/smkn3/pages/tkj/Guru"));
const TkjMikrotik = lazy(() => import("@/tenants/smkn3/pages/tkj/Mikrotik"));
const TkjKontak = lazy(() => import("@/tenants/smkn3/pages/tkj/Kontak"));
const Smkn3Berita = lazy(() => import("@/tenants/smkn3/pages/Berita"));
const Smkn3Guru = lazy(() => import("@/tenants/smkn3/pages/Guru"));
const Smkn3Kontak = lazy(() => import("@/tenants/smkn3/pages/tkj/Kontak"));

/* Unit generik untuk jurusan lain (tkr, tsm, titl, mp) */
const UnitLanding = lazy(() => import("@/tenants/smkn3/pages/unit/Landing"));
const UnitGuru = lazy(() => import("@/tenants/smkn3/pages/unit/Guru"));
const UnitBerita = lazy(() => import("@/tenants/smkn3/pages/unit/Berita"));

/* SPMB — Seleksi Penerimaan Murid Baru */
const SpmbLanding = lazy(() => import("@/tenants/smkn3/pages/spmb/Landing"));
const SpmbDaftar = lazy(() => import("@/tenants/smkn3/pages/spmb/Daftar"));

function Spinner() {
  return <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg">
    <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{borderColor:"var(--color-primary,#75350e)",borderTopColor:"transparent"}}/>
  </div>;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Spinner />}>{children}</Suspense>;
}

/** Guard public routes to specific business types */
function TenantGuard({ types, children }: { types: string[]; children: ReactNode }) {
  const { tenant, loading } = useTenant();
  if (loading) return <Spinner />;
  if (types.includes(tenant.businessType)) return <>{children}</>;
  if (types.includes("education") && tenant.subdomain === "smkn3") return <>{children}</>;
  return <Navigate to="/" replace />;
}

/** Route to the correct admin dashboard based on tenant business type */
function AdminDashboardRouter() {
  const { tenant } = useTenant();
  switch (tenant.businessType) {
    case "personal":
      return <Navigate to="/admin/lms" replace />;
    case "salon":
      return <Lazy><SalonAdminDashboard /></Lazy>;
    case "education":
      return <Lazy><Smkn3AdminDashboard /></Lazy>;
    case "photography":
    default:
      return <Lazy><AdaraAdminDashboard /></Lazy>;
  }
}

export default function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <TranslationProvider>
        <ThemeProvider>
          <TenantLayout>
            <Routes>
              <Route element={<ClientLayout />}>
                <Route index element={<Lazy><LandingRegistry /></Lazy>} />
                <Route path="explore" element={<Lazy><ExplorePageRouter /></Lazy>} />
                <Route path="login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
                <Route path="blog" element={<TenantGuard types={["personal"]}><Lazy><IpungBlog /></Lazy></TenantGuard>} />
                <Route path="blog/:slug" element={<TenantGuard types={["personal"]}><Lazy><IpungBlogDetail /></Lazy></TenantGuard>} />
                <Route path="learn" element={<TenantGuard types={["personal"]}><Lazy><IpungLearn /></Lazy></TenantGuard>} />
                <Route path="learn/:slug" element={<TenantGuard types={["personal"]}><Lazy><IpungModule /></Lazy></TenantGuard>} />
                <Route path="tools" element={<TenantGuard types={["personal"]}><Lazy><IpungTools /></Lazy></TenantGuard>} />
                <Route path="content" element={<TenantGuard types={["personal"]}><Lazy><IpungContent /></Lazy></TenantGuard>} />
                <Route path="live" element={<TenantGuard types={["personal"]}><Lazy><IpungLive /></Lazy></TenantGuard>} />
                <Route path="register" element={<RedirectIfAuth><RegisterPage /></RedirectIfAuth>} />
                <Route path="forgot-password" element={<ForgotPasswordPage />} />
                <Route path="reset-password" element={<ResetPasswordPage />} />
                <Route path="booking" element={<RequireAuth><Lazy><BookingPageRouter /></Lazy></RequireAuth>} />
                <Route path="dashboard" element={<RequireAuth><Lazy><DashboardPageRouter /></Lazy></RequireAuth>} />
                <Route path="profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                <Route path="verify-email" element={<RequireAuth><VerifyEmailPage /></RequireAuth>} />
              </Route>

              {/* SMKN3 public routes */}
              <Route path="berita" element={<TenantGuard types={["education"]}><Lazy><Smkn3Berita /></Lazy></TenantGuard>} />
              <Route path="guru" element={<TenantGuard types={["education"]}><Lazy><Smkn3Guru /></Lazy></TenantGuard>} />
              <Route path="kontak" element={<TenantGuard types={["education"]}><Lazy><Smkn3Kontak /></Lazy></TenantGuard>} />
              <Route path="spmb" element={<TenantGuard types={["education"]}><Lazy><SpmbLanding /></Lazy></TenantGuard>} />
              <Route path="spmb/daftar" element={<TenantGuard types={["education"]}><Lazy><SpmbDaftar /></Lazy></TenantGuard>} />
              <Route path="tkj" element={<TenantGuard types={["education"]}><Lazy><TkjLanding /></Lazy></TenantGuard>} />
              <Route path="tkj/berita" element={<TenantGuard types={["education"]}><Lazy><TkjBerita /></Lazy></TenantGuard>} />
              <Route path="tkj/guru" element={<TenantGuard types={["education"]}><Lazy><TkjGuru /></Lazy></TenantGuard>} />
              <Route path="tkj/mikrotik" element={<TenantGuard types={["education"]}><Lazy><TkjMikrotik /></Lazy></TenantGuard>} />
              <Route path="tkj/kontak" element={<TenantGuard types={["education"]}><Lazy><TkjKontak /></Lazy></TenantGuard>} />

              {/* Unit generik: tkr, tsm, titl, mp (path dinamis) */}
              <Route path=":unitSlug" element={<TenantGuard types={["education"]}><Lazy><UnitLanding /></Lazy></TenantGuard>} />
              <Route path=":unitSlug/berita" element={<TenantGuard types={["education"]}><Lazy><UnitBerita /></Lazy></TenantGuard>} />
              <Route path=":unitSlug/guru" element={<TenantGuard types={["education"]}><Lazy><UnitGuru /></Lazy></TenantGuard>} />
              <Route path=":unitSlug/kontak" element={<TenantGuard types={["education"]}><Lazy><Smkn3Kontak /></Lazy></TenantGuard>} />

              {/* ═══ ADMIN ROUTES — TENANT-ISOLATED ═══ */}
              <Route path="admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                <Route index element={<AdminDashboardRouter />} />
                <Route path="dashboard" element={<AdminDashboardRouter />} />

                {/* Photography admin routes */}
                <Route path="bookings" element={<AdminGuard types={["photography"]}><Lazy><AdaraAdminBookings /></Lazy></AdminGuard>} />
                <Route path="portfolio" element={<AdminGuard types={["photography"]}><Lazy><AdaraAdminPortfolio /></Lazy></AdminGuard>} />
                <Route path="homepage" element={<AdminGuard types={["photography"]}><Lazy><AdaraAdminHomepage /></Lazy></AdminGuard>} />
                <Route path="services" element={<AdminGuard types={["photography"]}><Lazy><AdaraAdminServices /></Lazy></AdminGuard>} />

                {/* Salon admin routes */}
                <Route path="salon-bookings" element={<AdminGuard types={["salon"]}><Lazy><SalonAdminBookings /></Lazy></AdminGuard>} />
                <Route path="salon-services" element={<AdminGuard types={["salon"]}><Lazy><SalonAdminServices /></Lazy></AdminGuard>} />

                {/* Users — shared: photography & salon */}
                <Route path="users" element={
                  <AdminGuard types={["photography", "salon"]}>
                    <Lazy>
                      <UsersRouter />
                    </Lazy>
                  </AdminGuard>
                } />

                {/* Personal admin routes */}
                <Route path="blog" element={<AdminGuard types={["personal"]}><Lazy><IpungAdminBlog /></Lazy></AdminGuard>} />
                <Route path="certifications" element={<AdminGuard types={["personal"]}><Lazy><IpungAdminCerts /></Lazy></AdminGuard>} />
                <Route path="modules" element={<AdminGuard types={["personal"]}><Lazy><IpungAdminModules /></Lazy></AdminGuard>} />
                <Route path="lms" element={<AdminGuard types={["personal"]}><Lazy><IpungAdminLMS /></Lazy></AdminGuard>} />
              </Route>

              <Route path="super-admin" element={<RequireAuth><RequireAdmin><Lazy><SuperAdminDashboard /></Lazy></RequireAdmin></RequireAuth>} />
              <Route path="*" element={<Lazy><LandingRegistry /></Lazy>} />
            </Routes>
          </TenantLayout>
        </ThemeProvider>
        </TranslationProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

/** Route to the correct Users admin page based on tenant */
function UsersRouter() {
  const { tenant } = useTenant();
  if (tenant.businessType === "salon") return <SalonAdminUsers />;
  return <AdaraAdminUsers />;
}
